// The stored sign-in: tokens from login.kast-dev.net in localStorage, the
// user read from the id token, and the URLs sign-in and sign-out go through.
// Pure functions; useSession owns the React side.

import { AUTH_STORAGE_KEYS } from "@jkast90/kast-common/refresh-session";

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export interface UserInfo {
  sub: string;
  email: string;
  name: string;
  /** Cognito groups from the id token. Empty when the token carries none. */
  groups: string[];
}

/**
 * Groups granting admin rights; must match AdminUser in api/src/auth.rs.
 * `admin` is the shared kast-dev group, `salvagers-admin` this app's. Client
 * checks only decide what to show: the API re-checks on every admin route.
 * Matched case-insensitively, like the API and the rest of the family.
 */
export const ADMIN_GROUPS = new Set(["admin", "salvagers-admin"]);

export const isAdmin = (user: UserInfo | null): boolean =>
  !!user && user.groups.some((group) => ADMIN_GROUPS.has(group.toLowerCase()));

/** Refresh this long before expiry. */
export const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000;

function base64UrlDecode(text: string): string {
  const padded = text.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(text.length / 4) * 4, "=");
  return decodeURIComponent(
    Array.from(atob(padded), (char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`).join(""),
  );
}

/** A JWT's claims, without verification (the API and Cognito verify). */
export function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1];
    return payload ? (JSON.parse(base64UrlDecode(payload)) as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Milliseconds until the token expires, 0 when expired or unreadable. */
export function msUntilExpiry(token: string): number {
  const claims = decodeJwt(token);
  const exp = typeof claims?.exp === "number" ? claims.exp : 0;
  return Math.max(0, exp * 1000 - Date.now());
}

/** The user an id token describes; only id tokens carry cognito:groups. */
export function userFromIdToken(idToken: string): UserInfo | null {
  const claims = decodeJwt(idToken);
  if (!claims || typeof claims.sub !== "string") return null;
  const groups = claims["cognito:groups"];
  const email = typeof claims.email === "string" ? claims.email : "";
  return {
    sub: claims.sub,
    email,
    name: typeof claims.name === "string" && claims.name ? claims.name : email,
    groups: Array.isArray(groups) ? groups.filter((g): g is string => typeof g === "string") : [],
  };
}

/** Tokens login.kast-dev.net put in the URL hash, or null when the hash is something else. */
export function tokensFromHash(hash: string): AuthTokens | null {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const accessToken = params.get("access_token");
  if (!accessToken) return null;
  return {
    accessToken,
    idToken: params.get("id_token") ?? "",
    refreshToken: params.get("refresh_token") ?? "",
  };
}

export function loadTokens(): AuthTokens | null {
  const accessToken = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
  if (!accessToken) return null;
  return {
    accessToken,
    idToken: localStorage.getItem(AUTH_STORAGE_KEYS.idToken) ?? "",
    refreshToken: localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken) ?? "",
  };
}

export function saveTokens(tokens: AuthTokens): void {
  localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, tokens.accessToken);
  if (tokens.idToken) localStorage.setItem(AUTH_STORAGE_KEYS.idToken, tokens.idToken);
  if (tokens.refreshToken) localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, tokens.refreshToken);
}

export function clearTokens(): void {
  for (const key of Object.values(AUTH_STORAGE_KEYS)) localStorage.removeItem(key);
}

/** The token the API wants: the id token (it carries the groups), else the access token. */
export const bearerToken = (tokens: AuthTokens | null): string | null =>
  tokens ? tokens.idToken || tokens.accessToken || null : null;

export const signInUrl = (loginUrl: string, returnTo: string): string =>
  `${loginUrl}/signin?redirect=${encodeURIComponent(returnTo)}`;

export const signOutUrl = (cognitoDomain: string, clientId: string, returnTo: string): string =>
  `${cognitoDomain}/logout?client_id=${encodeURIComponent(clientId)}&logout_uri=${encodeURIComponent(returnTo)}`;
